import styled from "@emotion/styled"
import { graphql } from "gatsby"
import { Seo } from "gatsby-theme-grape-blog/src/components/seo"
import React, { useEffect, useMemo, useState } from "react"
import { Header } from "../components/header"
import Layout, { OneColumn } from "../components/layout"
import PageHero from "../components/page-hero"
import { theme } from "../utils/theme"
import fallbackPoster from "../../content/assets/hero.jpg"

const PageContent = styled.div`
  background: #101820;
  color: #f9fbfc;
`

const MainSection = styled(OneColumn)`
  max-width: 66rem;
  padding-top: 3rem;
  padding-bottom: 4.5rem;
`

const IntroText = styled.p`
  margin: 0 0 1.5rem;
  color: #cde2eb !important;
`

const EventList = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;

  @media (min-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const EventCard = styled.a`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  background: #ffffff;
  color: ${theme.colors.text.body};
  border: 1px solid rgba(2, 48, 71, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background-image: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(2, 48, 71, 0.18);
    background-image: none;
  }
`

const Poster = styled.div`
  width: 100%;
  aspect-ratio: 16 / 8;
  background-color: #ecf9ff;
  background-image: ${({ image }) =>
    `linear-gradient(180deg, rgba(2, 48, 71, 0.08), rgba(2, 48, 71, 0.22)), url(${image})`};
  background-position: center;
  background-size: cover;

  @media (max-width: 620px) {
    aspect-ratio: 16 / 9;
  }
`

const EventBody = styled.div`
  display: grid;
  gap: 0.5rem;
  padding: 0.75rem 1rem 1rem;
`

const Tag = styled.span`
  justify-self: start;
  font-size: 0.65rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  min-height: 1.125rem;
  padding: 0 0.375rem;
  color: #101820;
  background: #f6c445;
  border-radius: 6px;
  font-weight: 700;
  text-transform: uppercase;
`

const EventTitle = styled.h2`
  font-size: 1.125rem;
  margin-top: 0;
  margin-bottom: 0;
  color: ${theme.colors.text.header} !important;
  line-height: 1.12;
`

const TimeGrid = styled.div`
  display: grid;
  gap: 0.375rem;
`

const DateGroup = styled.div`
  border-left: 3px solid ${theme.colors.primary};
  padding-left: 0.5rem;
`

const EventTime = styled.span`
  color: ${theme.colors.text.header};
  font-family: ${theme.fonts.header.fontFamily};
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.2;
`

const EmptyState = styled.div`
  padding: 1.5rem;
  border: 3px solid #f6c445;
  background: #ffffff;
  color: ${theme.colors.text.body};

  p {
    color: ${theme.colors.text.body};
  }
`

const formatInTimeZone = (date, timeZone) => {
  return new Intl.DateTimeFormat("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  }).format(date)
}

const useBrowserTimeZone = () => {
  const [timeZone, setTimeZone] = useState(null)

  useEffect(() => {
    try {
      setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? null)
    } catch (error) {
      setTimeZone(null)
    }
  }, [])

  return timeZone
}

const EventRow = ({ browserTimeZone, event }) => {
  const posterImage = event.thumbnailUrl ?? fallbackPoster
  const eventType = event.isOnline ? "Online event" : "In-person event"

  return (
    <EventCard href={event.url} target="_blank" rel="noreferrer">
      <Poster image={posterImage} aria-hidden="true" />
      <EventBody>
        <Tag>{eventType}</Tag>
        <EventTitle>{event.title}</EventTitle>
        <TimeGrid>
          {event.dates.map((dateOption) => {
            const date = new Date(dateOption.startsAt)

            return (
              <DateGroup key={dateOption.eventbriteId}>
                <EventTime>
                  {browserTimeZone
                    ? formatInTimeZone(date, browserTimeZone)
                    : ""}
                </EventTime>
              </DateGroup>
            )
          })}
        </TimeGrid>
      </EventBody>
    </EventCard>
  )
}

const EventsPage = ({ data }) => {
  const browserTimeZone = useBrowserTimeZone()
  const events = useMemo(() => {
    return data.allEvidenceStrongEvent.nodes
      .map((event) => {
        return {
          ...event,
          dates: event.dates.filter((date) => {
            return date.status !== "canceled"
          }),
        }
      })
      .filter((event) => {
        return event.status !== "canceled" && event.dates.length > 0
      })
  }, [data.allEvidenceStrongEvent.nodes])

  return (
    <Layout>
      <Seo
        title="Online Events"
        description="Online Evidence Strong events for lifters and coaches"
        slug="/events"
      />
      <PageContent>
        <Header />
        <PageHero title="Upcoming events" />
        <MainSection>
          <IntroText>
            Evidence Strong events turn strength sports science into practical
            framework for lifters and coaches.
          </IntroText>
          <IntroText>
            Join live webinars to learn evidence-based strategies and get your
            questions answered during live Q&amp;A.
          </IntroText>

          {events.length > 0 ? (
            <EventList>
              {events.map((event) => (
                <EventRow
                  key={event.eventbriteId}
                  browserTimeZone={browserTimeZone}
                  event={event}
                />
              ))}
            </EventList>
          ) : (
            <EmptyState>
              <h2>New dates are being scheduled</h2>
              <p>
                No live events are available right now. Check back soon for the
                next session.
              </p>
            </EmptyState>
          )}
        </MainSection>
      </PageContent>
    </Layout>
  )
}

export const query = graphql`
  {
    allEvidenceStrongEvent(sort: { fields: startsAt, order: ASC }) {
      nodes {
        eventbriteId
        title
        url
        thumbnailUrl
        isOnline
        status
        dates {
          eventbriteId
          startsAt
          status
        }
      }
    }
  }
`

export default EventsPage
